<script setup lang="ts">
import {nextTick, onMounted, reactive, ref, watch} from "vue"
import axios from "axios";

type StudentCard = {
  id: string
  name: string
}

// teacherLogin and studentLogin are not reactive, but it doesn't matter in this case
const urlParams = new URLSearchParams(window.location.search);
const teacherLogin = urlParams.get('teacher');
const studentLogin = urlParams.get('student');

const props = withDefaults(
  defineProps<{
    error?: string
    allowClever?: boolean
  }>(),
  {
    error: "",
    allowClever: false,
  },
)

const step = ref(1)
const showModal = ref(false)
const errorText = ref("")

const step1 = reactive({
  loadingStudents: false,
  nextTitle: "Next",
})

const step2 = reactive({
  type: "input",
  students: [] as StudentCard[],
  processing: false,
})

const step3 = reactive({
  processing: false,
})

const form = reactive({
  teacher: teacherLogin || "",
  student: studentLogin || "",
  studentId: "",
  password: "",
})

defineExpose({step, showModal, errorText, step1, step2, step3, form}) // tests use these, tho they shouldn't

const teacherInput = ref<HTMLInputElement>()
const studentInput = ref<HTMLInputElement>()
const passwordInput = ref<HTMLInputElement>()

watch(step, async (s) => {
  await nextTick()
  ;[teacherInput, studentInput, passwordInput][s - 1].value?.focus()
})

function onEnter() {
  if (step.value === 1) {
    stepOne()
  } else if (step.value === 2) {
    stepTwo()
  } else if (step.value === 3) {
    stepThree()
  }
}

function back() {
  if (step.value > 1) {
    step.value -= 1
  }
}

function showError(msg: string) {
  showModal.value = true
  errorText.value = msg
}

async function stepOne() {
  if (form.teacher) {

    step1.loadingStudents = true
    step1.nextTitle = "Loading..."
    try {
      const response = await axios.post("/student/login", {
        teacher: form.teacher,
        type: "unknown",
        step: step.value,
      })
      const data = response.data
      if (data.error) {
        showError(data.error)
      } else {
        step2.type = data.data.type
        if (step2.type === "input") {
          step2.students = []
        } else if (step2.type === "select") {
          step2.students = data.data.students
        }
        step.value = 2
        if (form.student.length > 0) {
          // ?student=login was provided, so look it up automatically
          step2.type = "input" // need this to disable the backend requirement for student id
          await stepTwo()
        }
      }
    } finally {
      step1.loadingStudents = false
      step1.nextTitle = "Next"
    }
  } else {
    showError("Enter your teacher's username")
  }
}

function selectStudent(student: StudentCard) {
  form.studentId = student.id
  form.student = student.name
  stepTwo()
}

async function stepTwo() {
  if (studentLogin || step2.type === "input") {

    if (form.student.length > 0) {
      step2.processing = true
      try {
        const response = await axios.post("/student/login", {
          teacher: form.teacher,
          type: step2.type,
          student: form.student,
          step: step.value,
        })
        const data = response.data
        if (data.error) {
          showError(data.error)
        } else {
          form.studentId = data.data.id
          step.value = 3
        }
      } finally {
        step2.processing = false
      }
    } else {
      showError("Enter your username")
    }
  } else {
    if (form.studentId) {
      step.value = 3
    } else {
      showError("Select your username")
    }
  }
}

async function stepThree() {
  if (form.password.length > 0) {
    step3.processing = true
    try {
      const response = await axios.post("/student/login", {
        teacher: form.teacher,
        type: step2.type,
        student: form.student,
        studentId: form.studentId,
        password: form.password,
        step: step.value,
      })
      const data = response.data
      if (data.error) {
        showError(data.error)
      } else {
        if (data.data.success) {
          window.location.reload()
        } else {
          showError("Something went wrong?")
        }
      }
    } finally {
      step3.processing = false
    }
  } else {
    showError("Enter your password")
  }
}

onMounted(() => {
  teacherInput.value?.focus()

  const error = props.error
  if (error) {
    console.error("Student login error: ", error)
    const messages = {
      clever_login_not_found:
        "Your Clever login works, but is not set up on Lyrics2Learn.  Please ask your teacher to set up your Clever login.",
    }
    const message = messages[error as keyof typeof messages] ?? `Unknown error while logging in: ${error}`
    showError(message)
  }

  if (teacherLogin) {
    // teacher login was provided in the query string, so auto-submit it to look up the teacher and get login cards
    stepOne()
  }
})

</script>

<template>
  <div class="container content-space-2 content-space-lg-3">
    <div class="flex-grow-1 mx-auto" style="max-width: 28rem;">

      <form role="form" method="post">
        <fieldset v-show="step === 1">
          <div class="text-center">
            <img class="avatar avatar-xl" src="/assets/images/lefty-new.png" alt=""/>
            <h1 class="h2">Student Login</h1>
            <p>Enter your teacher's username:</p>
          </div>

          <div>
            <div class="mb-3">
              <label for="tch_username"></label>
              <input
                id="tch_username" ref="teacherInput" v-model="form.teacher" type="text"
                class="form-control form-control-lg" name="form-first-name" placeholder="Your Teacher's Username"
                maxlength="20" required @keyup.enter="onEnter"
              >
            </div>
            <div class="d-grid mb-3">
              <button
                type="button" class="btn btn-primary btn-lg submit-step-1" :disabled="step1.loadingStudents"
                @click.prevent="stepOne()"
              >{{ step1.nextTitle }}
              </button>
            </div>
          </div>

          <div v-show="allowClever">
            <div class="text-bold text-center mt-3 mb-3">&mdash; or &mdash;</div>

            <a href="/clever/login" class="btn btn-primary btn-lg clever-button">
              <div class="icon"></div>
              <div class="buttonText">Login With Clever</div>
            </a>
          </div>
        </fieldset>

        <fieldset v-show="step === 2">
          <div class="mb-3">
            <div class="text-center">
              <img class="avatar avatar-xl" src="/assets/images/lefty-new.png" alt=""/>
              <h3>Student Login</h3>
              <p v-if="step2.type === 'input'">Type your username:</p>
              <p v-if="step2.type === 'select'">Select your username:</p>
            </div>
          </div>
          <!----FIX THIS---->
          <div class="container text-center">
            <div v-if="step2.type === 'input'" class="form-group">
              <label class="sr-only" for="student_username">Username</label>
              <input
                ref="studentInput" v-model="form.student" type="text" name="student_username"
                placeholder="Username..." class="form-first-name form-control"
                maxlength="20" @keyup.enter="onEnter"
              >
            </div>
            <div v-if="step2.type === 'select'">
              <div
                v-for="student in step2.students" :key="student.id" class="col-sm-4 btn"
                @click="selectStudent(student)"
              >
                <a
                  class="text-center btn btn-primary fs-4 text-white"
                  :class="{ stud_selected: form.studentId === student.id }"
                >
                  <i class="bi bi-person-vcard fs-1 text-white"></i>
                  {{ student.name }}
                </a>
              </div>
              <!----<div class="clear"></div>--->
            </div>
            <div class="mb-3">
              <div id="step2_btns">
                <button
                  type="button" class="btn btn-success text-center" :disabled="step2.processing"
                  @click.prevent="back()"
                >Previous
                </button>
                <button
                  v-if="step2.type === 'input'" type="button" class="btn submit-step-2"
                  :disabled="step2.processing" @click.prevent="stepTwo()"
                >
                  <span v-show="step2.processing">Loading..</span>
                  <span v-show="!step2.processing">Next</span>
                </button>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset v-show="step === 3" id="step3">
          <div class="text-center">
            <img class="avatar avatar-xl" src="/assets/images/lefty-new.png" alt=""/>
            <div class="mb-3">
              <h3>Student Login</h3>
              <p>Enter Your Password:</p>
            </div>
          </div>
          <div class="mb-3">
            <div class="form-group">
              <label for="stud_password">Password</label>
              <input
                ref="passwordInput"
                v-model="form.password"
                type="text"
                name="stud_password"
                placeholder="Password..."
                autocomplete="false"
                spellcheck="false"
                class="js-toggle-password form-control form-control-lg"
                @keyup.enter="onEnter"
              >
            </div>
            <p></p>
            <div class="text-center mb-3">
              <button
                type="button" class="btn btn-success mr-2" :disabled="step3.processing"
                @click.prevent="back()"
              >Previous
              </button>
              <button
                type="button" class="btn btn-primary submit-step-3" :disabled="step3.processing"
                @click.prevent="stepThree()"
              >
                <span v-show="!step3.processing">Sign me in!</span>
                <span v-show="step3.processing">Signing in...</span>
              </button>
            </div>
          </div>
        </fieldset>
      </form>
    </div>

    <LeftyPopup v-if="showModal" :show-header="false"><h3>{{ errorText }}</h3></LeftyPopup>

  </div>
</template>

<style lang="scss" scoped>
// Modal related below
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, .5);
  display: table;
  transition: opacity .3s ease;
}

.modal-wrapper {
  display: table-cell;
  //      vertical-align: middle;
}

.show_err_modal {
  .modal-content {
    background: none !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  .lefty-speech-wrap {
    background: url(/images/lefty-speech.png) no-repeat left bottom;
    background-size: 100px;
    min-height: 130px;
  }

  .lefty-speech {
    background: #fff;
    padding: 15px;
    border-radius: 10px;
    margin-left: 85px;
    margin-bottom: 80px;
  }

  .modal-content {
    background: none !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  @media (min-width: 768px) {
    .modal-dialog {
      width: 70% !important;
    }
  }
}


.clever-button {
  $icon-size: 48px;

  position: relative;
  display: flex;
  padding: 0;
  width: 100%;
  height: calc(#{$icon-size} + 2px);

  .icon {
    background: url(/assets/images/clever_logo.png) white 50% 50% no-repeat;
    background-size: 50%;
    position: absolute;
    left: 0;
    top: 0;
    width: $icon-size;
    height: $icon-size;
  }

  .buttonText {
    margin: auto;
    width: 100%;
  }
}
</style>
